<template>
  <!--
    Phone tab bar (design 1h): the office's first four pinned apps (layout.tabBarIds) + "More".
    Presentational: AppShell passes the rows and runs everything. A tab is a router-link (it
    navigates itself) and also emits `select` so the shell can close what is open; "More" emits
    `more` (the shell opens the drawer). Never reads $route.
  -->
  <nav class="app-tabbar" aria-label="Main apps" data-test="app-shell-tabbar-panel">
    <router-link
      v-for="tab in tabs"
      :key="tab.id"
      :to="tab.to"
      class="app-tabbar__tab"
      :class="{ 'is-on': tab.id === activeId && !moreActive }"
      :aria-current="tab.id === activeId ? 'page' : null"
      :aria-label="tab.count ? `${tab.label}, ${tab.count}` : tab.label"
      :data-test="`app-tab-${tab.id}`"
      draggable="false"
      @click.native="$emit('select', tab.item)"
    >
      <span class="app-tabbar__pill">
        <AppIcon :name="tab.icon" :size="21" />
        <span v-if="tab.count" class="app-tabbar__badge" data-test="app-tab-count">{{ tab.count }}</span>
        <span v-else-if="tab.dot" class="app-tabbar__dot" data-test="app-tab-dot" />
      </span>
      <span class="app-tabbar__label">{{ tab.short }}</span>
    </router-link>

    <button
      type="button"
      class="app-tabbar__tab"
      :class="{ 'is-on': moreActive }"
      aria-haspopup="dialog"
      aria-label="More apps and settings"
      data-test="app-tab-more"
      @click="$emit('more')"
    >
      <span class="app-tabbar__pill"><AppIcon name="grid" :size="21" /></span>
      <span class="app-tabbar__label">More</span>
    </button>
  </nav>
</template>

<script>
/**
 * App shell — phone tab bar (design 1h). Plan ~/.claude/plans/nested-conjuring-flurry.md §3 / §6.
 *
 *   props  items   resolved rows (≤ 4 shown) { id, label, shortLabel, icon, to, count?, dot? }
 *          activeId     the app the route is on (aria-current on its tab)
 *          moreActive   the active app is not one of the four, or the drawer is open — "More" is
 *                       highlighted and no app tab is
 *   emits  select(item), more
 *
 * Fixed to the bottom: 56px + the iPhone home-indicator inset; Layout.scss already pads `.wrap`
 * by 56px in phone mode.
 */
import AppIcon from './AppIcon.vue';
import { countLabel } from './AppNavItem.vue';
import { TAB_BAR_SIZE } from '@/utils/nav/layout';

export default {
  name: 'AppTabBar',
  components: { AppIcon },
  props: {
    items: { type: Array, default: () => [] },
    activeId: { type: String, default: null },
    moreActive: { type: Boolean, default: false },
  },
  computed: {
    tabs() {
      return this.items
        .filter(row => row && row.id && row.to)
        .slice(0, TAB_BAR_SIZE)
        .map(row => ({
          id: row.id,
          item: row,
          to: row.to,
          icon: row.icon,
          label: row.label,
          short: row.shortLabel || row.label,
          count: countLabel(row.count),
          dot: row.dot === true,
        }));
    },
  },
};
</script>

<style lang="scss" scoped>
@import './shell';

.app-tabbar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: $shell-z-chrome;
  display: flex;
  align-items: flex-start;
  height: calc(#{$shell-tabbar-height} + env(safe-area-inset-bottom, 0px));
  padding: 0 4px env(safe-area-inset-bottom, 0px);
  background: $fu-surface;
  border-top: 1px solid $fu-border;
  font-family: $fu-font-sans;
  @media print { display: none; }
}

.app-tabbar__tab {
  @include fu-button-reset;
  @include fu-focus-ring;
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  min-height: $shell-touch-target;
  height: $shell-tabbar-height - 1px; // under the 1px top border
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  border-radius: 12px;
  color: $fu-neutral-500;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;

  &:hover,
  &:focus { text-decoration: none; color: $fu-neutral-600; }

  &.is-on {
    .app-tabbar__pill { background: $fu-brand-50; color: $fu-brand-700; }
    .app-tabbar__label { color: $fu-text; font-weight: $fu-fw-bold; }
  }
}

.app-tabbar__pill {
  position: relative;
  width: 52px;
  height: 30px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  transition: background $fu-dur-fast $fu-ease-out;
}

.app-tabbar__label {
  max-width: 100%;
  padding: 0 2px;
  font-size: 11px;
  font-weight: $fu-fw-medium;
  line-height: 1.15;
  color: $fu-neutral-500;
  @include fu-ellipsis;
}

// Design: the count sits just right of the icon's centre, on the pill's top edge.
.app-tabbar__badge {
  position: absolute;
  top: -6px;
  left: calc(50% + 8px);
  @include shell-count-badge;
}

.app-tabbar__dot {
  position: absolute;
  top: -1px;
  left: calc(50% + 10px);
  @include shell-dot;
}
</style>

<style lang="scss">
// The Teamio Agent button (agent-panel.css: fixed, bottom 88px, z 9999) must clear the tab bar plus
// the iPhone home-indicator inset (56 + 34 = 90px). Only while this bar is on screen (phone shell).
body:has(.app-tabbar) .copilotKitSidebar.ac-root {
  bottom: max(88px, calc(72px + env(safe-area-inset-bottom, 0px))) !important;
}
</style>
